import { useState } from "react";
import CardChipIcon from "../../assets/images/icon.card.chip.svg";
import { changeDefaultCard, deleteCard } from "../../api/cash/cash.api";
import { Trash2, Check } from "lucide-react";

/**
 * 등록 카드 1장.
 *
 * @param canDelete 삭제 가능 여부. **마지막 1장은 삭제할 수 없다** — 결제수단이 0장이 되면
 *   다음 정산 결제가 카드 미등록으로 실패한다. 서버도 같은 규칙으로 막지만(400 + 사유),
 *   버튼 단계에서 이유를 먼저 알려주는 편이 낫다.
 *
 * 2026-08-06 변경: 종전에는 **기본카드에 삭제 버튼을 아예 안 그렸다.** 그런데 최초 등록 카드는
 * 무조건 기본카드가 되므로(서버 `isDefault = isNew`) **카드가 1장인 고객은 삭제 수단이 없었다**
 * — 이번 CS("카드를 삭제할 수 없다")의 직접 원인이다.
 * 판정 기준을 '기본카드인가'에서 **'마지막 1장인가'** 로 바꿨고, 기본카드를 지우면 서버가
 * 남은 카드 중 최근 등록분을 기본으로 자동 승격한다.
 *
 * @param readOnly 조회 전용(환불 화면처럼 '어떤 카드인지'만 보여주는 자리). 조작 버튼을 그리지 않는다.
 *   `onRefresh` 가 없는 호출부에서 삭제 버튼이 눌리면 갱신 단계에서 터지므로 기본값이 아니라 명시한다.
 */
const CardDesign = ({ cardInfo, onRefresh, canDelete = false, readOnly = false }) => {
    // 오류는 alert 대신 카드 위에 남긴다 — alert 은 닫는 순간 사라져서
    // "마지막 카드라 삭제할 수 없다" 같은 안내를 다시 읽을 수 없다.
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const handleSetDefault = async () => {
        if (!window.confirm("이 카드를 기본 결제 카드로 설정하시겠습니까?")) return;
        setError("");
        setBusy(true);
        try {
            await changeDefaultCard(cardInfo.id);
            onRefresh();
        } catch (e) {
            setError(e?.message || "기본 카드를 변경하지 못했습니다.");
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!canDelete) {
            setError("등록된 카드가 1장뿐이라 삭제할 수 없습니다. 새 카드를 먼저 등록해 주세요.");
            return;
        }
        const warn = cardInfo.isDefault
            ? "이 카드는 기본 결제 카드입니다.\n삭제하면 남은 카드 중 최근에 등록한 카드가 기본 결제 카드가 됩니다.\n\n정말로 삭제하시겠습니까?"
            : "정말로 이 카드를 삭제하시겠습니까?";
        if (!window.confirm(warn)) return;
        setError("");
        setBusy(true);
        try {
            await deleteCard(cardInfo.id);
            onRefresh();
        } catch (e) {
            // 실패 사유(마지막 1장 / 권한 없음 / 카드사 거부)를 그대로 보여준다.
            // 종전에는 성공·실패 모두 "카드가 삭제되었습니다" 가 떴다.
            setError(e?.message || "카드를 삭제하지 못했습니다.");
        } finally {
            setBusy(false);
        }
    };

    const formatCardNumber = (num) => {
        if (!num) return "**** **** **** ****";
        return num.toString().replace(/(\d{4})/g, '$1').trim();
    };

    return (
        <div className="relative w-[320px] h-[200px] rounded-2xl p-6 flex flex-col justify-between shadow-md hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden group">
            <div className="absolute -right-12 -bottom-12 w-40 h-40 bg-white opacity-5 rounded-full pointer-events-none"></div>

            <div className="flex justify-between items-start z-10">
                {/* 카드가 여러 장이면 마스킹 번호만으로는 어떤 카드인지 떠올리기 어렵다 → 카드사 이름을 칩 옆에 크게 둔다. */}
                <div className="flex items-center gap-2 min-w-0">
                    <img src={CardChipIcon} alt="card-chip" className="w-10 shrink-0 opacity-80" />
                    {cardInfo.cardCompany && (
                        <span className="truncate text-[15px] font-bold text-white/95 drop-shadow">
                            {cardInfo.cardCompany}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {readOnly ? (
                        cardInfo.isDefault && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-white/20 text-white text-[11px] font-bold rounded-full backdrop-blur-sm border border-white/20">
                                <Check className="w-3.5 h-3.5" strokeWidth={3} />
                                기본 결제 카드
                            </span>
                        )
                    ) : (
                      <>
                    {cardInfo.isDefault ? (
                        <span className="flex items-center gap-1 px-3 py-1 bg-white/20 text-white text-[11px] font-bold rounded-full backdrop-blur-sm border border-white/20">
                            <Check className="w-3.5 h-3.5" strokeWidth={3} />
                            기본 결제 카드
                        </span>
                    ) : (
                        <button
                            onClick={handleSetDefault}
                            disabled={busy}
                            className="px-3 py-1 bg-transparent text-white/70 text-[11px] font-medium rounded-full border border-white/30 hover:bg-white/20 hover:text-white transition-colors disabled:opacity-40"
                        >
                            기본으로 설정
                        </button>
                    )}

                    {/*
                      마지막 1장은 삭제 버튼을 흐리게 두고 눌렀을 때 이유를 알려준다.
                      숨겨 버리면 "왜 못 지우는지"를 알 수 없다 — 종전 기본카드 처리가 그랬고 그게 이번 CS 였다.
                    */}
                    <button
                        onClick={handleDelete}
                        disabled={busy}
                        className={`p-1.5 rounded-full transition-colors backdrop-blur-sm ml-1 text-white ${
                            canDelete
                                ? "bg-red-500/80 hover:bg-red-600"
                                : "bg-white/10 text-white/40 cursor-not-allowed"
                        } disabled:opacity-40`}
                        title={canDelete ? "카드 삭제" : "마지막 남은 카드는 삭제할 수 없습니다"}
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                      </>
                    )}
                </div>
            </div>

            <div className="z-10 mt-2">
                <div className="font-medium text-[22px] text-white drop-shadow-md">
                    {formatCardNumber(cardInfo.cardNumber)}
                </div>
            </div>

            <div className="flex justify-between items-end z-10 mt-2">
                <div className="flex flex-col">
                    <span className="text-[10px] text-white/50 font-bold uppercase tracking-wider mb-0.5">Card Type</span>
                    <span className="font-medium text-white/90 text-sm">{cardInfo.cardType}카드</span>
                </div>
            </div>

            {/* 실패 사유는 카드 안에 남긴다(닫으면 사라지는 alert 대신). 직접 닫을 수 있게 X 를 둔다. */}
            {error && (
                <div className="absolute inset-x-3 bottom-3 z-20 flex items-start gap-2 rounded-lg bg-red-600/95 px-3 py-2 text-[11px] leading-snug text-white shadow-lg">
                    <span className="flex-1">{error}</span>
                    <button
                        onClick={() => setError("")}
                        className="shrink-0 font-bold text-white/80 hover:text-white"
                        aria-label="오류 메시지 닫기"
                    >
                        ✕
                    </button>
                </div>
            )}
        </div>
    );
};

export default CardDesign;